import React, { useCallback, useEffect, useRef, useState } from 'react';
import i18next from 'i18next';
import { QuerySuggestion } from '../../@types/QuerySuggestion';

import useQuerySugesstion from '../../Hooks/useQuerySuggestion';
import SuggestionList from './SuggestionList';
interface SearchBarProps {
  pathname: string;
  url: string;
  placeholder?: string;
}

const SearchBar = ({ pathname, url, placeholder }: SearchBarProps) => {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [queryCompletionIndex, setQueryCompletionIndex] = useState(-1);
  const [inputIsFocused, setInputIsFocused] = useState(false);
  const { response } = useQuerySugesstion(url, query);
  const timeout = useRef<number>(undefined);

  useEffect(() => {
    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      setQuery(search);
    }, 150) as unknown as number;
    return () => {
      clearTimeout(timeout.current);
    };
  }, [search]);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.currentTarget.value),
    []
  );

  const performSearch = useCallback((value: string) => {
    const searchString = value ? `?query=${value}` : '';

    if (window.location.pathname === pathname && window.WaykePubSub2.publish) {
      if (window.WaykePubSub2.publish) {
        window.WaykePubSub2.publish('onQueryUpdate', searchString);
      }
      setSearch('');
    } else {
      window.location.assign(`${pathname}${searchString}`);
    }
  }, []);

  const onSelectSuggestion = useCallback((suggestion: QuerySuggestion) => {
    performSearch(suggestion.label);
  }, []);

  const onSearch = useCallback(() => {
    performSearch(search);
  }, [search]);

  const goToSearchPage = () => window.location.assign(`${pathname}`);

  const onSearchPage = window.location.pathname.indexOf(pathname) > -1;

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        if (queryCompletionIndex > -1) {
          const match = response?.suggestions.find((_, i) => i === queryCompletionIndex);
          if (match) {
            onSelectSuggestion(match);
            setQueryCompletionIndex(-1);
          }
        } else {
          onSearch();
        }
      }
    },
    [search, queryCompletionIndex, inputIsFocused]
  );

  const onFocusSearchField = () => {
    setInputIsFocused(true);
  };

  const getNextIndex = (key: string, currentIndex: number, maxIndex: number) => {
    let index = key === 'ArrowUp' ? currentIndex - 1 : currentIndex + 1;

    if (index < 0 && key === 'ArrowUp') {
      index = -1;
    } else if (index >= maxIndex) {
      index = maxIndex;
    }
    return index;
  };

  const onInitQueryCompleter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.stopPropagation();
      e.preventDefault();
      if (response) {
        setQueryCompletionIndex(
          getNextIndex(e.key, queryCompletionIndex, response?.suggestions.length - 1)
        );
      }
    }
  };

  const component = (
    <div className="header__search">
      <div className="search">
        <div className="search__bar">
          <div className="search__icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 16"
              className="icon icon--block"
            >
              <path d="M15.9 15.1l-4.8-4.7c1-1.1 1.6-2.5 1.6-4.1C12.7 2.9 9.9.1 6.4.1S0 2.9 0 6.4c0 3.4 2.8 6.2 6.3 6.2 1.6 0 3-.6 4.2-1.5l4.8 4.7c.1.1.2.1.3.1.1 0 .2 0 .3-.1.1-.3.1-.5 0-.7zM.8 6.4C.8 3.4 3.3 1 6.3 1s5.5 2.4 5.5 5.3-2.5 5.3-5.5 5.3S.8 9.3.8 6.4z" />
            </svg>
          </div>
          <input
            id="search_bar_input"
            type="text"
            placeholder={placeholder || i18next.t('searchBar.placeholder') || undefined}
            title={placeholder || i18next.t('searchBar.title') || undefined}
            className="search__input theme theme__font--regular"
            value={search}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onKeyUp={onInitQueryCompleter}
            autoComplete="off"
            onFocus={onFocusSearchField}
          />
          <div className="search__action">
            <button
              id="search_button"
              className="button button--primary"
              title={i18next.t('searchBar.button.title') || ''}
              onClick={onSearch}
            >
              <span className="button__content">{i18next.t('searchBar.button.text')}</span>
            </button>
          </div>
        </div>
        {response && (
          <SuggestionList
            currentIndex={queryCompletionIndex}
            querySuggestion={response}
            onShowAll={onSearchPage ? undefined : goToSearchPage}
            onSelectSuggestion={onSelectSuggestion}
          />
        )}
      </div>
    </div>
  );

  return component;
};

export default SearchBar;
